import { AddCircleOutlined, RemoveCircleOutlined } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { getPricing } from '../../api/branchesAPI';
import {
  createEstimate,
  createEstimateItem,
  deleteEstimateItem,
  updateEstimate,
  updateEstimateItem
} from '../../api/estimatesAPI';
import { getCurrentBranch } from '../../api/usersAPI';

// Creates an empty row object that dynamically assigns tax exemption
  const createEmptyRow = (taxExemptionStatus) => ({
    id: null,
    description: '',
    quantity: '',
    part_price: '',
    labor_price: '',
    tax_exempt: taxExemptionStatus
  });

/** Modal form for creating or editing an estimate.
 * @param {boolean} isOpen - Whether the modal is open or closed
 * @param {function} onClose - Function to close the modal
 * @param {number} vehicleId - ID of the vehicle associated with the estimate
 * @param {object} estimate - Existing estimate object to edit (optional)
 * @param {Array<Object>} owners - Owners of this vehicle
 * @param {function} refreshFunction - Function to refresh the parent component (optional)
 */
function EstimateModal({ isOpen, onClose, vehicleId, estimate=null, owners=[], refreshFunction=null }) {
  // Array of estimate item values (i.e. id, description, quantity, part price, labor price, tax exemption)
  const [rows, setRows] = useState([]);

  // Array of estimate item IDs that need to be deleted from DB
  const [itemsToDelete, setItemsToDelete] = useState([]);

  // Fetched pricing options for the current branch or use business pricing if not available
  const [defaultPricing, setDefaultPricing] = useState([]);

  // Default tax exemption based on estimate owner
  const [defaultTaxExemption, setDefaultTaxExemption] = useState(false);

  // Object of user that the estimate was made for
  const [estimateOwner, setEstimateOwner] = useState(null);

  // Form submission errors
  const [errors, setErrors] = useState({});

  /** Add new blank row for estimate. */
  const handleAddRow = () => {
    setRows((currentRows) => [
      ...currentRows,
      createEmptyRow(defaultTaxExemption),
    ]);
  };

  /** Remove row containing an estimate item. */
  const handleRemoveRow = (index, id) => {
    if (rows.length === 1) return;  // Ensure at least one row exists
    // Remove the row and reset rows useState
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows);

    // Add estimate item id to array that will be deleted upon form submission
    // An id means that it's an existing estimate item and will need to be deleted with a DELETE request, hence tracking
    // No id means that the row doesn't already exist and doesn't need a DELETE request
    if (id) {
      setItemsToDelete((currentItems) => [
        ...currentItems,
        id,
      ]);
    }
  };

  /**
   *
   * @type {(function(*, *): void)|*}
   */
  const updateRow = useCallback((index, updates) => {
    setRows((currentRows) =>
      currentRows.map((row, rowIndex) =>
        rowIndex === index
          ? { ...row, ...updates }
          : row
      )
    );
  }, []);

  /** Update specified row's field with a new value. */
  const updateRowField = useCallback((index, field, value) => {
    setRows((currentRows) =>
      currentRows.map((row, rowIndex) =>
        rowIndex === index
          ? { ...row, [field]: value }
          : row
      )
    );
  }, []);

  /**
   * Changes the customer the estimate was created for. Impacts tax exemption and is used on estimate document.
   * @param owner
   */
  const handleOwnerChange = useCallback((owner) => {
    // If owner gets deselected, set estimate owner to null but keep tax exemption
    if (owner === null) {
      setEstimateOwner(null);
      return;
    }

    setRows((currentRows) =>
      currentRows.map((row) => ({
        ...row,
        tax_exempt: owner.tax_exempt,
      }))
    );

    setDefaultTaxExemption(owner.tax_exempt);
    setEstimateOwner(owner);
  }, []);

  /**
   * Resets variables for next use and tells parent component to close modal.
   * @param event
   * @param reason
   */
  const handleClose = (event, reason) => {
    // Prevent escaping modal with backdrop click
    if (reason && reason === 'backdropClick') {
      return;
    }

    // Reset some of the form variables to get ready for next use
    setRows([createEmptyRow(defaultTaxExemption)]);
    setItemsToDelete([]);
    setEstimateOwner(null)
    setErrors({})

    onClose();
  }

  /** Handle estimate form submission. */
  const handleSubmit = async () => {
    // Remove rows that do not have a description
    // NOTE: If you edit an existing estimate item to have no description, submitting the form shouldn't affect it
    const estimate_items = rows.filter(row => row.description);

    try {
      if (estimateOwner === null) {
        setErrors({ error: 'No estimate owner set' });
        return;
      }
      if (!estimate) {  // Existing estimate wasn't provided - create new estimate and estimate items
        const currentBranchResponse = await getCurrentBranch();
        const branchId = currentBranchResponse.data.current_branch;

        // Construct values for creating an estimate, which can take estimate item values and create in same request
        let values = {
          vehicle_id: vehicleId,
          branch_id: branchId,
          estimate_items,
          estimate_owner_id: estimateOwner.id,
        };
        await createEstimate(values);
      } else if (estimate) {  // Existing estimate was provided - add, edit or remove estimate items to estimate
        // Edit existing or create new estimate item
        const updatedEstimate = {
          branch: estimate.branch_details.id,
          vehicle: estimate.vehicle.id,
          estimate_owner: estimateOwner.id
        }
        await updateEstimate(estimate.id, updatedEstimate);
        for (let i =0; i < rows.length; i++) {
          let value = {
            estimate: estimate.id,
            description: rows[i].description,
            quantity: rows[i].quantity,
            part_price: rows[i].part_price,
            labor_price: rows[i].labor_price,
            tax_exempt: rows[i].tax_exempt,
          };

          // Create new or update existing estimate item depending on if there was an id
          if (rows[i].id == null) {  // id is null, create new item
            await createEstimateItem(value);
          } else {  // id exists, update existing item
            await updateEstimateItem(rows[i].id, value);
          }
        }

        // Delete existing estimate items from database that were removed from estimate form
        if (itemsToDelete) {
          for (const itemId of itemsToDelete) {
            await deleteEstimateItem(itemId);
          }
        }
      }

      // Reset useState values
      // TODO: Is this needed? Already reset when I close.
      setRows([createEmptyRow(defaultTaxExemption)]);
      setItemsToDelete([]);

      // Refresh the parent component if a refresh function was provided
      if (refreshFunction) { refreshFunction(); }

      // Close modal
      handleClose();
    } catch (error) {
      if (error.response) {
        setErrors(error.response.data);
        console.error('Response error:', error.response.data);
      } else if (error.request) {
        setErrors({ error: 'The server did not respond.' });
        console.error('Request error:', error.request);
      } else {
        setErrors({ error: error.message });
        console.error('Error:', error.message);
      }
    }
  }

  useEffect(() => {
    if (!isOpen) return;

    const initialOwner =
      estimate?.estimate_owner_details ??
      (owners?.length === 1 ? owners[0] : null);

    console.log(initialOwner)
    setEstimateOwner(initialOwner);

    if (estimate) {
      setRows(
        estimate.estimate_items.map((item) => ({ ...item }))
      );
    } else {
      setRows([
        createEmptyRow(initialOwner?.tax_exempt ?? false)
      ]);
    }

    setDefaultTaxExemption(initialOwner?.tax_exempt ?? false);
    setItemsToDelete([]);
    setErrors({});

    async function handlePricingGet() {
      try {
        const response = await getPricing();
        setDefaultPricing(response.data.default_pricing ?? []);
      } catch (error) {
        console.error('Error fetching default pricing:', error);
      }
    }

    handlePricingGet();
  }, [isOpen, estimate, owners]);

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby='estimate-modal-title'
        aria-describedby='estimate-modal-description'
      >
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 775,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4
          }}
        >
          <Typography variant='h6' gutterBottom>
            {estimate ? 'Edit Estimate' : 'Create Estimate'}
          </Typography>
          {errors.non_field_errors && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {errors.non_field_errors[0]}
            </Alert>
          )}
          {errors.error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {errors.error}
            </Alert>
          )}
          <Autocomplete
              slotProps={{
                listbox: { style: { maxHeight: 250, overflow: 'auto' }}
              }}
              size='small'
              sx={{ my: 2 }}
              id='owner-autocomplete'
              value={estimateOwner}
              options={owners}
              getOptionLabel={(option) => {
                if (option.tax_exempt) {
                  return option.name + ' (Tax Exempt)'
                }
                return option.name
              }}
              renderInput={(params) => <TextField {...params} label='Prepared For' />}
              onChange={(e, v) => {
                console.log(e);
                console.log(v);
                handleOwnerChange(v)
              }}
            />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px' }}>
            <Typography display='inline' sx={{ flexGrow: 1 }}>Description</Typography>
            <Typography display='inline' sx={{ width: '60px' }}>Qty</Typography>
            <Typography display='inline' sx={{ width: '100px' }}>Part Price</Typography>
            <Typography display='inline' sx={{ width: '100px' }}>Labor Price</Typography>
            <Typography display='inline' sx={{ width: '100px' }}>Tax Exempt</Typography>
            <Typography display='inline' sx={{ width: '40px' }}></Typography>
          </Box>
          {rows.map((row, index) => (
            <Box
              key={index}
              data-id={row.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '5px',
                marginY: '10px'
              }}
            >
              <Autocomplete
                disablePortal
                freeSolo
                size='small'
                sx={{ flexGrow: 1 }}
                options={defaultPricing}
                getOptionLabel={(option) => option?.name ?? ''}
                inputValue={row.description}
                onInputChange={(event, newInputValue) => {
                  updateRowField(index, 'description', newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                  />
                )}
                onChange={(event, newValue) => {
                  if (!newValue || typeof newValue === 'string') return;

                  updateRow(index, {
                    description: newValue.name ?? '',
                    quantity: newValue.quantity ?? '',
                    part_price: newValue.part_price ?? '',
                    labor_price: newValue.labor_price ?? '',
                    tax_exempt: defaultTaxExemption,
                  });
                }}
              />
              <TextField
                sx={{ width: '60px' }}
                size='small'
                value={row.quantity}
                onChange={(e) => updateRowField(index, 'quantity', e.target.value)}
              />
              <TextField
                sx={{ width: '100px' }}
                size='small'
                value={row.part_price}
                onChange={(e) => updateRowField(index, 'part_price', e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                  },
                }}
              />
              <TextField
                sx={{ width: '100px' }}
                size='small'
                value={row.labor_price}
                onChange={(e) => updateRowField(index, 'labor_price', e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                  },
                }}
              />
              <Checkbox
                sx={{ width: '100px' }}
                checked={row.tax_exempt}
                onChange={(e) => updateRowField(index, 'tax_exempt', e.target.checked)}
              />
              <IconButton onClick={() => handleRemoveRow(index, row.id)} disabled={rows.length === 1}>
                <RemoveCircleOutlined />
              </IconButton>
            </Box>
          ))}
          <Box sx={{ display:'Flex', gap: '20px' }}>
            <Button onClick={handleAddRow} startIcon={<AddCircleOutlined />}>Add Item</Button>
            <Button onClick={handleSubmit}>Submit</Button>
            <Button color='error' onClick={handleClose}>Cancel</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default EstimateModal;